import Element, { layoutToParams } from '../Element';
import Renderable from '../../../Component/Renderable';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import ButtonComponent from '../../../Component/UI/ButtonComponent';
import { StartAnimation } from '../../../System/Animation/AnimationHelper';
import Interactable from '../../../Component/Interactable';
import UIItem, { ITextData, IImageData } from '../UIItem';
import Point from '../../../Utility/Point';
import ElementComponent from '../../../Component/UI/Element';
import { PlaySound } from '../../../System/Audio/AudioHelper';
import Game from '../../../Game';
import { AudioTrackType } from '../../../System/Audio/AudioClip';

/**
 * @param name - Name of button
 * @param text - ITextData for text element
 * @param background - IImageData for background element
 * @param button - IImageData for button element
 */
export interface IUIButtonData {
    name: string;
    cacheKey?: string;
    text?: ITextData;
    background?: IImageData;
    button?: IImageData;
    [key: string]: any;
}

// TODO: replace old button with this
export default class UIButton extends Element {
    protected backgroundParams: IImageData;
    protected buttonParams: IImageData;
    protected textParams: ITextData;

    public onCreate(params: IUIButtonData): void {
        this.addComponent<ElementComponent>(ElementComponent, {
            ...params,
            ...layoutToParams(params.layout),
        });
        this.addComponent<Renderable>(Renderable, params);
        this.addComponent<Interactable>(Interactable, { buttonMode: true });
        this.addComponent<ButtonComponent>(ButtonComponent, params);

        this.on(GameObjectEvent.SELECTED, this.clicked);
        this.on(GameObjectEvent.POINTER_DOWN, this.selected);
        this.on(GameObjectEvent.POINTER_UP, this.released);
        this.on(GameObjectEvent.POINTER_OUT, this.released);

        if (params.background) {
            this.backgroundParams = params.background;
            const background: GameObject<PIXI.Container> = UIItem.createImage(
                params.background
            );
            this.addChild(background);
        }

        if (params.button) {
            this.buttonParams = params.button;
            const button: GameObject<PIXI.Container> = UIItem.createImage(
                this.buttonParams
            );
            if (this.background) {
                this.background.addChild(button);
            } else {
                this.addChild(button);
            }
        }

        if (params.text) {
            this.textParams = params.text;
            const text: GameObject<PIXI.Container> = UIItem.createText(
                this.textParams
            );
            if (this.button) {
                this.button.addChild(text);
            } else if (this.background) {
                this.background.addChild(text);
            } else {
                this.addChild(text);
            }
        }
    }

    protected readonly clicked = (): void => {
        const buttonComponent: ButtonComponent = this.getComponent(
            ButtonComponent
        );

        // onClick callback
        if (buttonComponent.onClick) {
            buttonComponent.onClick();
        }
    }

    protected readonly selected = (): void => {
        const buttonComponent: ButtonComponent = this.getComponent(
            ButtonComponent
        );

        // Play Audio
        if (buttonComponent.onClickAudio) {
            PlaySound(buttonComponent.onClickAudio);
        }

        if (buttonComponent.onClickAnimation) {
            StartAnimation(this.button, buttonComponent.onClickAnimation, {
                scale: new Point(0.95, 0.95),
            });
        }
    }

    protected readonly released = (): void => {
        const buttonComponent: ButtonComponent = this.getComponent(
            ButtonComponent
        );
        if (buttonComponent.onReleaseAnimation) {
            StartAnimation(this.button, buttonComponent.onReleaseAnimation, {
                scale: new Point(1, 1),
            });
        }
    }

    public onDestroy(): void {
        this.off(GameObjectEvent.SELECTED, this.clicked);
        this.off(GameObjectEvent.POINTER_DOWN, this.selected);
        this.off(GameObjectEvent.POINTER_UP, this.released);
        this.off(GameObjectEvent.POINTER_OUT, this.released);
    }

    public set enabled(value: boolean) {
        this.getComponent(Interactable).enabled = value;
    }

    public set visible(value: boolean) {
        this.getComponent(Renderable).visible = value;
    }

    public get background(): GameObject {
        if (this.backgroundParams)
            return this.findChildByName(this.backgroundParams.name);

        return undefined;
    }

    public get button(): GameObject {
        if (this.buttonParams) {
            return this.findChildByName(this.buttonParams.name);
        }
        return undefined;
    }

    public get text(): GameObject {
        if (this.textParams) {
            return this.findChildByName(this.textParams.name);
        }
        return undefined;
    }
}
